import { Phone, EnvelopeSimple, CheckCircle, ChatText, FacebookLogo, InstagramLogo, WhatsappLogo } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { PHONE, PHONE_HREF, EMAIL, EMAIL_HREF, SMS_HREF, LICENSED_INSURED, SOCIAL_LINKS } from "@/data/content";
import { LogoMark } from "@/components/site/Logo";
import { CITIES } from "@/data/cities";
import { useSectionNav } from "@/lib/nav";

export default function ContactFooter() {
  const scrollTo = useSectionNav();
  return (
    <>
      <section id="contact" className="py-16 md:py-28 bg-emerald-700 grain" data-testid="contact-banner">
        <div className="max-w-4xl mx-auto px-6 md:px-10 text-center relative z-10">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-100 mb-4">Ready for a cleaner space?</p>
          <h2 className="font-heading text-4xl md:text-5xl font-medium tracking-tight text-white">Ready for a Cleaner Home?</h2>
          <p className="mt-5 text-white/85 text-lg max-w-2xl mx-auto">
            Request your free estimate today and let Explus Cleaning take care of the rest.
          </p>
          <div className="mt-9 flex flex-col sm:flex-row justify-center gap-4">
            <button onClick={() => scrollTo("#quote")} className="rounded-full bg-white text-emerald-800 font-semibold px-8 py-4 flex items-center justify-center gap-2 hover:-translate-y-0.5 hover:shadow-xl transition-all" data-testid="cta-quote-btn">
              Request a Free Quote
            </button>
            <a href={PHONE_HREF} className="rounded-full border border-white text-white font-semibold px-8 py-4 flex items-center justify-center gap-2 hover:bg-white/10 transition-colors" data-testid="cta-call-btn">
              <span className="call-ring"><Phone className="call-shake" size={20} weight="fill" /></span> Call Now
            </a>
            <a href={SMS_HREF} className="rounded-full border border-white text-white font-semibold px-8 py-4 flex items-center justify-center gap-2 hover:bg-white/10 transition-colors" data-testid="cta-text-btn">
              <ChatText size={20} weight="fill" /> Text Us
            </a>
          </div>
        </div>
      </section>

      <footer className="bg-primary pt-16 pb-8 text-primary-foreground/70" data-testid="site-footer">
        <div className="max-w-6xl mx-auto px-6 md:px-10 grid sm:grid-cols-2 md:grid-cols-4 gap-10 pb-10">
          <div>
            <span className="inline-flex items-center gap-2 font-heading text-2xl font-semibold text-white">
              <LogoMark className="w-8 h-8" gradient />
              Explus <span className="text-emerald-300 -ml-1 font-normal">Cleaning</span>
              <span className="ml-1 text-[0.5em] font-semibold tracking-wider self-start mt-1 text-emerald-300">LLC</span>
            </span>
            <p className="mt-4 leading-relaxed max-w-sm">
              Professional residential and commercial cleaning services in Everett and surrounding areas.
            </p>
            <span className="mt-4 inline-flex items-center gap-2 rounded-full border border-emerald-300/40 px-3 py-1 text-sm font-medium text-emerald-300" data-testid="footer-insured">
              <CheckCircle size={16} weight="fill" /> {LICENSED_INSURED}
            </span>
            <div className="mt-5 flex items-center gap-3" data-testid="footer-social">
              <a href={SOCIAL_LINKS.facebook} target="_blank" rel="noopener noreferrer" aria-label="Explus Cleaning on Facebook" data-testid="footer-social-facebook"
                className="w-10 h-10 rounded-full border border-white/15 bg-white/5 flex items-center justify-center text-white/80 hover:bg-emerald-600 hover:text-white hover:border-emerald-400 hover:-translate-y-0.5 transition-all">
                <FacebookLogo size={20} weight="fill" />
              </a>
              <a href={SOCIAL_LINKS.instagram} target="_blank" rel="noopener noreferrer" aria-label="Explus Cleaning on Instagram" data-testid="footer-social-instagram"
                className="w-10 h-10 rounded-full border border-white/15 bg-white/5 flex items-center justify-center text-white/80 hover:bg-emerald-600 hover:text-white hover:border-emerald-400 hover:-translate-y-0.5 transition-all">
                <InstagramLogo size={20} weight="fill" />
              </a>
              <a href={SOCIAL_LINKS.whatsapp} target="_blank" rel="noopener noreferrer" aria-label="Chat with Explus Cleaning on WhatsApp" data-testid="footer-social-whatsapp"
                className="w-10 h-10 rounded-full border border-white/15 bg-white/5 flex items-center justify-center text-white/80 hover:bg-emerald-600 hover:text-white hover:border-emerald-400 hover:-translate-y-0.5 transition-all">
                <WhatsappLogo size={20} weight="fill" />
              </a>
            </div>
          </div>
          <div>
            <h3 className="text-white font-semibold mb-4">Quick Links</h3>
            <ul className="space-y-2">
              {[["Services", "#services"], ["About Us", "#about"], ["Free Quote", "#quote"], ["Contact", "#contact"]].map(([l, h]) => (
                <li key={h}><button onClick={() => scrollTo(h)} className="hover:text-emerald-300 transition-colors">{l}</button></li>
              ))}
            </ul>
          </div>
          <div>
            <h3 className="text-white font-semibold mb-4">Contact</h3>
            <ul className="space-y-2">
              <li><a href={PHONE_HREF} className="hover:text-emerald-300 transition-colors">{PHONE}</a></li>
              <li><a href={EMAIL_HREF} className="hover:text-emerald-300 transition-colors break-all">{EMAIL}</a></li>
              <li>Everett, Washington</li>
            </ul>
          </div>
          <div>
            <h3 className="text-white font-semibold mb-4">Service Areas</h3>
            <ul className="grid grid-cols-2 gap-x-4 gap-y-2" data-testid="footer-service-areas">
              {CITIES.map((c) => (
                <li key={c.slug}>
                  <Link to={`/${c.slug}`} className="hover:text-emerald-300 transition-colors">{c.name}, WA</Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
        <div className="border-t border-white/10 pt-6 text-center text-sm">
          <p className="flex items-center justify-center gap-2 text-emerald-300/90 font-medium mb-2" data-testid="footer-trust-line">
            <CheckCircle size={15} weight="fill" /> {LICENSED_INSURED} · Serving Everett &amp; surrounding areas
          </p>
          © {new Date().getFullYear()} Explus Cleaning LLC. All rights reserved.
          {" · "}
          <a href="/admin/login" className="hover:text-emerald-300 transition-colors" data-testid="footer-admin-link">Admin</a>
        </div>
      </footer>

      <a href={PHONE_HREF} aria-label="Call Explus Cleaning" data-testid="floating-call"
        className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-emerald-600 text-white border-2 border-white shadow-xl flex items-center justify-center hover:scale-110 transition-transform">
        <span className="call-ring"><Phone className="call-shake" size={24} weight="fill" /></span>
      </a>
      <a href={SOCIAL_LINKS.whatsapp} target="_blank" rel="noopener noreferrer" aria-label="Chat with Explus Cleaning on WhatsApp" data-testid="floating-whatsapp"
        className="fixed bottom-6 left-6 z-50 rounded-full bg-emerald-600 text-white border-2 border-white shadow-xl pl-3.5 pr-5 py-3 flex items-center gap-2 font-semibold text-sm hover:-translate-y-0.5 hover:shadow-2xl hover:bg-emerald-500 transition-all">
        <WhatsappLogo size={22} weight="fill" /> Chat with us
      </a>
    </>
  );
}
